import { renderMetaTags, TitleMetaLinkTag, useQuerySubscription, Image as DatoImage } from "react-datocms";
import { GetStaticProps } from "next";
import { Api } from "@/api";
import {
  AwardsLayout as Layout,
  AwardsSection as Section,
  SEO,
  Container,
  FaqsSection,
  HorizontalRule,
  HtmlToElement,
  ArrowAnchorButton,
} from "@/components";
import { DATOCMS_API_TOKEN, PHOTOGRAPHY_AWARDS_ALLOW_SUBMISSIONS_OVERRIDE, ROUTES } from "@/config";
import { accordionFragment, AccordionFragmentType, metaTagsFragment, responsiveImageFragment } from "@/modules";
import { AwardsCategoryCMS, ImageType, Subscription } from "@/types";

interface CmsFields {
  categories: AwardsCategoryCMS[];
  infoSection: Nullable<{
    leadLine: string;
    title: string;
    description: string;
    backgroundImage: ImageType;
  }>;
  faqs: AccordionFragmentType;
  seo: TitleMetaLinkTag[];
  partners?: {
    heading: string;
    text: string;
    image: ImageType;
  }[];
  allowSubmissions: boolean;
}

type Props = {
  preview: boolean;
  subscription: Subscription<CmsFields>;
};

const Awards2022CategoriesPage: React.FC<Props> = ({ preview, subscription }) => {
  const { data } = useQuerySubscription(subscription);
  if (!data) return null;

  const { categories, infoSection, partners, faqs, seo, allowSubmissions } = data;

  return (
    <Layout preview={preview} allowSubmissions={allowSubmissions}>
      <SEO>
        {renderMetaTags(seo)}
        <title>Categories and Prizes for the Narrative International Photography Awards | Narrative</title>
      </SEO>

      <section className="awards-categories pt-28 lg:pt-50 pb-25 lg:pb-30">
        <div className="w-full max-w-[1232px] px-[20px] mx-auto">
          <h1 className="text-48 leading-[107%] mb-8 lg:text-70 lg:mb-12">
            Categories
            <br /> and Prizes
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 md:gap-y-25 gap-y-18">
            {categories.map(({ title, slug, text, image, group, allowSubmission: allowCategorySubmission }) => {
              const groupCategories = categories.filter((category) => category.group === group);
              const isFistOccurrenceOfCategory = groupCategories[0].slug === slug;

              return (
                <article
                  key={slug}
                  className="flex flex-col scroll-mt-20 lg:scroll-mt-24"
                  {...(isFistOccurrenceOfCategory && {
                    id: group
                      .toLowerCase()
                      .replace(/\s+/g, "-")
                      .replace(/[.,/'#!$%^&*;:{}=\_`~()]/g, ""),
                  })}
                >
                  <div className="h-[76px]">
                    {isFistOccurrenceOfCategory && <h2 className="text-34 leading-tighter mb-8 lg:text-40">{group}</h2>}
                  </div>

                  <div className="aspect-w-12 aspect-h-7 mb-8.5">
                    <DatoImage data={{ ...image.responsiveImage }} layout="fill" objectFit="cover" />
                  </div>
                  <h3 className="text-h3 leading-tighter mb-4">{group === title ? title : `${group}: ${title}`}</h3>
                  <HtmlToElement className="prose flex-1 leading-normal mb-5">{text}</HtmlToElement>
                  {allowSubmissions && allowCategorySubmission && (
                    <ArrowAnchorButton href={`/${ROUTES.AWARDS.ENTER.SLUG}?category=${slug}`} variant="primary">
                      Submit: {title.toLowerCase()}
                    </ArrowAnchorButton>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {infoSection && (
        <Section className="relative">
          <div className="absolute top-0 right-0 bottom-0 left-0">
            <DatoImage data={{ ...infoSection.backgroundImage.responsiveImage }} layout="fill" objectFit="cover" />
          </div>
          <Container className="py-32">
            <div className="relative">
              <div>
                <p className="text-36">{infoSection.leadLine}</p>
                <h2 className="text-70 font-semibold leading-tighter mb-2">{infoSection.title}</h2>
                <HtmlToElement className="prose leading-normal max-w-[500px]">{infoSection.description}</HtmlToElement>
              </div>
            </div>
          </Container>
        </Section>
      )}

      {partners && (
        <Section>
          <Container>
            <HorizontalRule className="mb-6" />
            <h2 className="text-40 leading-tighter mb-12">Thank you to our awards partners</h2>
            <div className="grid gap-x-5 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
              {partners.map(({ heading, text, image }, i) => (
                <article key={i}>
                  <div className="aspect-w-14 aspect-h-8 mb-8 bg-[#1b1b1b]">
                    <DatoImage
                      usePlaceholder={false}
                      data={{ ...image.responsiveImage }}
                      style={{
                        position: "absolute",
                        inset: "50% auto auto 50%",
                        height: "auto",
                        maxWidth: "52%",
                        transform: "translate(-50%, -50%)",
                      }}
                    />
                  </div>
                  <HtmlToElement element="h3" className="text-h3 leading-tighter mb-5">
                    {heading}
                  </HtmlToElement>
                  <HtmlToElement className="prose leading-normal">{text}</HtmlToElement>
                </article>
              ))}
            </div>
          </Container>
        </Section>
      )}

      {faqs && <FaqsSection {...faqs} />}
    </Layout>
  );
};

/**
 * Static props
 */
export const getStaticProps: GetStaticProps<Props> = async ({ preview = false }) => {
  // WARNING: this query fetches "pageAwardsTwentyTwentyTwo" which while saying 2022 is used for 2023 and just the content changed
  const graphqlRequest = {
    preview,
    query: `
      {
        page: pageAwardsTwentyTwentyTwo {
          allowSubmissions
          categories {
            title
            group
            slug
            text
            allowSubmission
            image {
              url
              alt
              width
              height
              responsiveImage(imgixParams: { auto: format }) {
                ...responsiveImageFragment
              }
            }
          }
          infoSection {
            leadLine
            title
            description
            backgroundImage {
              responsiveImage(imgixParams: { auto: format }) {
                ...responsiveImageFragment
              }
            }
          }
          partners {
            heading
            text
            image {
              url
              alt
              width
              height
              responsiveImage(imgixParams: { auto: format }) {
                ...responsiveImageFragment
              }
            }
          }
          faqs: categoriesFaqs {
            ...accordionFragment
          }
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
        }
      }
      ${metaTagsFragment}
      ${accordionFragment}
      ${responsiveImageFragment}
    `,
  };

  const {
    data: {
      data: { page: initialData },
    },
  } = await Api.datocmsGraphqlRequest<{ page: CmsFields }>(graphqlRequest);

  const data = {
    ...initialData,
    allowSubmissions:
      PHOTOGRAPHY_AWARDS_ALLOW_SUBMISSIONS_OVERRIDE !== undefined
        ? PHOTOGRAPHY_AWARDS_ALLOW_SUBMISSIONS_OVERRIDE === "true"
        : initialData.allowSubmissions,
  };

  return {
    props: {
      preview,
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData: data,
            token: DATOCMS_API_TOKEN,
          }
        : {
            initialData: data,
            enabled: false,
          },
    },
  };
};

export default Awards2022CategoriesPage;
